import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DnsRoundedIcon from "@mui/icons-material/DnsRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { compactActionSx, surfaceSx } from "./ownerCenterUi";

const EMPTY = {
  name: "",
  provider: "Render",
  kind: "shared",
  monthlyCost: "",
  currency: "USD",
  billingDay: 1,
  includedCapacity: "",
  usedCapacity: 0,
  capacityUnitLabel: "cuentas",
  initiativeIds: [],
  clientIds: [],
  notes: "",
};

function money(value, currency) {
  const code = String(currency || "USD").toUpperCase();
  const amount = Number(value || 0);
  if (/^[A-Z]{3}$/.test(code)) {
    return new Intl.NumberFormat("es-AR", { style: "currency", currency: code, maximumFractionDigits: 2 }).format(amount);
  }
  return `${new Intl.NumberFormat("es-AR", { maximumFractionDigits: 2 }).format(amount)} ${code}`;
}

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function InfrastructureDialog({ open, pool, initiatives, clients, onClose, onSaved }) {
  const [form, setForm] = React.useState(EMPTY);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setError("");
    setForm(pool ? {
      name: pool.name || "",
      provider: pool.provider || "",
      kind: pool.kind || "shared",
      monthlyCost: pool.monthlyCost ?? "",
      currency: pool.currency || "USD",
      billingDay: pool.billingDay || 1,
      includedCapacity: pool.includedCapacity ?? pool.capacity?.included ?? "",
      usedCapacity: pool.usedCapacity ?? pool.capacity?.used ?? 0,
      capacityUnitLabel: pool.capacityUnitLabel || pool.capacity?.unitLabel || "cuentas",
      initiativeIds: (pool.initiatives || pool.initiativeIds || []).map(idOf).filter(Boolean),
      clientIds: (pool.clients || pool.clientIds || []).map(idOf).filter(Boolean),
      notes: pool.notes || "",
    } : EMPTY);
  }, [open, pool]);

  const set = (key) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      const payload = {
        ...form,
        monthlyCost: Number(form.monthlyCost),
        billingDay: Number(form.billingDay),
        includedCapacity: form.includedCapacity === "" ? null : Number(form.includedCapacity),
        usedCapacity: Number(form.usedCapacity || 0),
        capacityUnitLabel: String(form.capacityUnitLabel || "cuentas").trim() || "cuentas",
        initiativeIds: form.initiativeIds,
        clientIds: form.clientIds,
      };
      if (pool) await ownerApi.updateInfrastructure(idOf(pool), payload);
      else await ownerApi.createInfrastructure(payload);
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(formatApiError(err, "No pude guardar la infraestructura."));
    } finally { setSaving(false); }
  };

  const dedicatedAssigned = form.initiativeIds.length + form.clientIds.length;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 1000 }}>{pool ? "Editar infraestructura" : "Nueva infraestructura"}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.35} sx={{ pt: .6 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
            <TextField fullWidth label="Nombre" value={form.name} onChange={set("name")} placeholder="Servidor SaaS WebProfit" />
            <TextField fullWidth label="Proveedor" value={form.provider} onChange={set("provider")} placeholder="Render" />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
            <TextField select fullWidth label="Tipo" value={form.kind} onChange={set("kind")}>
              <MenuItem value="shared">Compartido</MenuItem>
              <MenuItem value="dedicated">Dedicado</MenuItem>
            </TextField>
            <TextField fullWidth type="number" label="Costo mensual" value={form.monthlyCost} onChange={set("monthlyCost")} inputProps={{ min: 0, step: "0.01" }} />
            <TextField select sx={{ minWidth: 110 }} label="Moneda" value={form.currency} onChange={set("currency")}>
              {["USD", "ARS", "USDT", "USDC"].map((currency) => <MenuItem key={currency} value={currency}>{currency}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField type="number" label="Día de cobro del proveedor" value={form.billingDay} onChange={set("billingDay")} inputProps={{ min: 1, max: 31 }} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
            <TextField
              fullWidth
              type="number"
              label="Capacidad incluida"
              value={form.includedCapacity}
              onChange={set("includedCapacity")}
              inputProps={{ min: 1, step: 1 }}
              placeholder="2"
              helperText="Opcional. Ej.: el plan incluye hasta 2 cuentas por el mismo precio."
            />
            <TextField
              fullWidth
              type="number"
              label="Capacidad usada ahora"
              value={form.usedCapacity}
              onChange={set("usedCapacity")}
              inputProps={{ min: 0, step: 1 }}
              placeholder="1"
            />
            <TextField fullWidth label="Unidad" value={form.capacityUnitLabel} onChange={set("capacityUnitLabel")} placeholder="cuentas" />
          </Stack>
          <FormControl fullWidth>
            <InputLabel id="infra-projects-label">Proyectos que usan este recurso</InputLabel>
            <Select
              labelId="infra-projects-label"
              multiple
              value={form.initiativeIds}
              onChange={set("initiativeIds")}
              label="Proyectos que usan este recurso"
              renderValue={(selected) => selected.map((id) => initiatives.find((row) => idOf(row) === id)?.name || id).join(", ")}
            >
              {initiatives.map((row) => {
                const id = idOf(row);
                return <MenuItem key={id} value={id} disabled={form.kind === "dedicated" && dedicatedAssigned >= 1 && !form.initiativeIds.includes(id)}><Checkbox checked={form.initiativeIds.includes(id)} /><ListItemText primary={row.name} /></MenuItem>;
              })}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="infra-clients-label">Clientes SaaS directos</InputLabel>
            <Select
              labelId="infra-clients-label"
              multiple
              value={form.clientIds}
              onChange={set("clientIds")}
              label="Clientes SaaS directos"
              renderValue={(selected) => selected.map((id) => clients.find((row) => idOf(row) === id)?.name || id).join(", ")}
            >
              {clients.map((row) => {
                const id = idOf(row);
                return <MenuItem key={id} value={id} disabled={form.kind === "dedicated" && dedicatedAssigned >= 1 && !form.clientIds.includes(id)}><Checkbox checked={form.clientIds.includes(id)} /><ListItemText primary={row.name} /></MenuItem>;
              })}
            </Select>
          </FormControl>
          <Alert severity="info" icon={false} sx={{ py: .55, fontSize: ".76rem" }}>
            {form.kind === "shared"
              ? "El costo real se registra una sola vez. Los proyectos/clientes vinculados no equivalen necesariamente a cuentas consumidas: la capacidad del plan se controla por separado."
              : "Un recurso dedicado puede quedar asociado a un único cliente o proyecto."}
          </Alert>
          <TextField multiline minRows={2} label="Notas" value={form.notes} onChange={set("notes")} placeholder="Plan, capacidad, límites, observaciones…" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving || !form.name.trim() || !(Number(form.monthlyCost) > 0)}>{saving ? "Guardando…" : "Guardar"}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function OwnerInfrastructurePanel({ focusRecurringPlanId = "" }) {
  const [data, setData] = React.useState({ pools: [], summary: {} });
  const [initiatives, setInitiatives] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [dialog, setDialog] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState("");
  const handledRecurringFocusRef = React.useRef("");

  const load = React.useCallback(async () => {
    setError("");
    try {
      const [infra, projectData, clientData] = await Promise.all([
        ownerApi.listInfrastructure(),
        ownerApi.listInitiatives({ status: "", limit: 250 }),
        ownerApi.listClients({ status: "", limit: 250 }),
      ]);
      setData(infra || { pools: [], summary: {} });
      setInitiatives(projectData?.initiatives || []);
      setClients(clientData?.clients || []);
    } catch (err) {
      setError(formatApiError(err, "No pude cargar la infraestructura."));
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    if (!focusRecurringPlanId || loading || handledRecurringFocusRef.current === String(focusRecurringPlanId)) return;
    const pool = (data?.pools || []).find((candidate) => String(candidate.recurringPlanId || "") === String(focusRecurringPlanId));
    if (!pool) return;
    handledRecurringFocusRef.current = String(focusRecurringPlanId);
    setEditing(pool);
    setDialog(true);
  }, [focusRecurringPlanId, loading, data?.pools]);

  const toggle = async (pool) => {
    const id = idOf(pool);
    setBusy(id); setError("");
    try {
      await ownerApi.updateInfrastructure(id, { status: pool.status === "paused" ? "active" : "paused" });
      await load();
    } catch (err) { setError(formatApiError(err, "No pude actualizar la infraestructura.")); }
    finally { setBusy(""); }
  };

  const end = async (pool) => {
    if (!window.confirm(`Finalizar ${pool.name}? El gasto recurrente futuro se detiene, pero el historial queda intacto.`)) return;
    const id = idOf(pool);
    setBusy(id); setError("");
    try { await ownerApi.endInfrastructure(id); await load(); }
    catch (err) { setError(formatApiError(err, "No pude finalizar la infraestructura.")); }
    finally { setBusy(""); }
  };

  const totals = data?.summary?.activeMonthlyCostByCurrency || {};
  const pools = data?.pools || [];

  return (
    <Box sx={{ ...surfaceSx, overflow: "hidden", mt: 1.2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.65, pt: 1.35, pb: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap>
            <DnsRoundedIcon color="primary" sx={{ fontSize: 19 }} />
            <Typography sx={{ fontWeight: 1000, fontSize: ".94rem" }}>Infraestructura</Typography>
            {Object.entries(totals).map(([currency, amount]) => <Chip key={currency} size="small" label={`${money(amount, currency)}/mes`} sx={{ height: 21, fontWeight: 900, fontSize: ".63rem" }} />)}
          </Stack>
          <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".71rem", mt: .15 }}>Cada servidor/costo existe una sola vez. Compartirlo no multiplica el gasto real.</Typography>
        </Box>
        <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => { setEditing(null); setDialog(true); }} sx={compactActionSx}>Servidor</Button>
      </Stack>
      {error ? <Alert severity="error" sx={{ mx: 1.4, mb: 1 }}>{error}</Alert> : null}
      {loading ? <Typography sx={{ px: 1.65, pb: 1.4, color: "text.secondary", fontSize: ".76rem" }}>Cargando…</Typography> : !pools.length ? (
        <Typography sx={{ px: 1.65, pb: 1.5, color: "text.secondary", fontWeight: 700, fontSize: ".76rem" }}>Todavía no cargaste infraestructura compartida o dedicada.</Typography>
      ) : (
        <Stack divider={<Divider flexItem />}>
          {pools.map((pool) => {
            const id = idOf(pool);
            const count = Number(pool.allocation?.beneficiaryCount || 0);
            const projectCount = Number(pool.allocation?.linkedProjectCount ?? pool.initiatives?.length ?? 0);
            const clientCount = Number(pool.allocation?.linkedClientCount ?? pool.clients?.length ?? 0);
            const perUnit = pool.allocation?.monthlyCostPerBeneficiary;
            const capacity = pool.capacity || {};
            const names = [...(pool.initiatives || []).map((x) => x.name), ...(pool.clients || []).map((x) => x.name)];
            return (
              <Box key={id} sx={{ px: 1.65, py: 1.05, opacity: pool.status === "paused" ? .65 : 1 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={.55} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontWeight: 950, fontSize: ".84rem" }}>{pool.name}</Typography>
                      <Chip size="small" label={pool.kind === "shared" ? "Compartido" : "Dedicado"} sx={{ height: 20, fontSize: ".61rem", fontWeight: 900 }} />
                      {pool.status === "paused" ? <Chip size="small" label="Pausado" sx={{ height: 20, fontSize: ".61rem", fontWeight: 900 }} /> : null}
                    </Stack>
                    <Typography sx={{ mt: .2, fontWeight: 950, fontSize: ".82rem" }}>{money(pool.monthlyCost, pool.currency)}/mes <Typography component="span" sx={{ color: "text.secondary", fontSize: ".69rem", fontWeight: 750 }}>{pool.provider ? ` · ${pool.provider}` : ""}</Typography></Typography>
                    <Typography sx={{ mt: .15, color: "text.secondary", fontWeight: 740, fontSize: ".69rem" }}>
                      {projectCount || clientCount
                        ? `${projectCount ? `${projectCount} proyecto${projectCount === 1 ? "" : "s"}` : ""}${projectCount && clientCount ? " · " : ""}${clientCount ? `${clientCount} cliente${clientCount === 1 ? "" : "s"}` : ""}${pool.kind === "shared" && perUnit != null && count ? ` · ${money(perUnit, pool.currency)} por beneficiario (analítico)` : ""}`
                        : "Sin proyectos/clientes vinculados todavía"}
                    </Typography>
                    {capacity.included != null ? (
                      <Typography sx={{ mt: .1, color: capacity.isFull ? "warning.main" : "text.secondary", fontWeight: 850, fontSize: ".69rem" }}>
                        {`${capacity.used || 0}/${capacity.included} ${capacity.unitLabel || "cuentas"} usadas · ${capacity.remaining || 0} disponibles al mismo precio`}
                      </Typography>
                    ) : null}
                    {names.length ? <Typography noWrap sx={{ mt: .12, color: "text.secondary", fontWeight: 700, fontSize: ".66rem" }}>{names.join(" · ")}</Typography> : null}
                  </Box>
                  <Stack direction="row" spacing={.25} sx={{ flexShrink: 0 }}>
                    <Tooltip title="Editar"><Button size="small" onClick={() => { setEditing(pool); setDialog(true); }} sx={{ minWidth: 34, px: .5 }}><EditRoundedIcon fontSize="small" /></Button></Tooltip>
                    <Tooltip title={pool.status === "paused" ? "Reactivar" : "Pausar"}><span><Button size="small" disabled={busy === id} onClick={() => toggle(pool)} sx={{ minWidth: 34, px: .5 }}>{pool.status === "paused" ? <PlayCircleOutlineRoundedIcon fontSize="small" /> : <PauseCircleOutlineRoundedIcon fontSize="small" />}</Button></span></Tooltip>
                    <Tooltip title="Finalizar"><span><Button color="error" size="small" disabled={busy === id} onClick={() => end(pool)} sx={{ minWidth: 34, px: .5 }}><StopCircleRoundedIcon fontSize="small" /></Button></span></Tooltip>
                  </Stack>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
      <InfrastructureDialog open={dialog} pool={editing} initiatives={initiatives} clients={clients} onClose={() => setDialog(false)} onSaved={load} />
    </Box>
  );
}
